<template>
  <header :class="['header-style sticky-top', { 'elevated': isElevated }]">
    <div class="container">
      <b-navbar class="nav-bar-style" toggleable="lg" v-b-scrollspy="scrollspyConfig">
        <b-navbar-toggle target="navbar" aria-label="Expandir navegação">
          <span class="navbar-toggler-icon d-flex flex-column justify-content-around">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </b-navbar-toggle>
        <b-navbar-brand @click="goToTop">
          <img src="@img/header/logo-nav.svg" alt="Observatório do Câncer RS">
          <span class="pl-1 pl-sm-3 align-middle">Observatório do Câncer RS</span>
        </b-navbar-brand>
        <b-collapse id="navbar" ref="navbar" is-nav>
          <b-navbar-nav class="ml-auto">
            <template v-for="(item, index) in navlinks">
              <b-nav-item-dropdown
                v-if="item.subnav"
                ref="navitem"
                variant="primary"
                menu-class="navbar-nav__dropdown-content"
                no-caret
                :key="index">
                <template #button-content>
                  <div class="navbar-nav__dropdown-label-content">
                    <span class="navbar-nav__dropdown-text">{{item.label}}</span>
                    <span class="navbar-nav__dropdown-icon"></span>
                  </div>
                </template>
                <b-dropdown-item
                  v-for="(subitem, subindex) in item.subnav"
                  @click="closeNav"
                  :to="subitem.link"
                  link-class="nav-link"
                  :key="subindex">{{ subitem.label }}</b-dropdown-item>
              </b-nav-item-dropdown>
              <b-nav-item
                v-else
                :data-placeholder="item.isPlaceholder"
                ref="navitem"
                @click="closeNav"
                :to="item.link"
                :key="index">{{ item.label }}</b-nav-item>
            </template>
            <span class="navbar-nav__active-link-marker" ref="marker"></span>
            <span class="navbar-nav__active-link-marker-hider"></span>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>
    </div>
  </header>
</template>

<script>
import Vue from 'vue';
import debounce from '@/utils/debounce.js';

let scrollCallback;

export default {
  name: 'app-header',
  props: {
    navlinks: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    scrollspyConfig: {
      offset: 53, // Offset do tamanho do header
    },
    isElevated: window.scrollY > 0,
  }),
  mounted() {
    this.$root.$on('bv::scrollspy::activate', this.setActiveMenuItem);
    scrollCallback = debounce(() => { this.isElevated = window.scrollY > 0; }, 50);
    window.addEventListener('scroll', scrollCallback);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', scrollCallback);
  },
  methods: {
    goToTop() {
      window.scrollTo(0, 0);
    },
    closeNav() {
      this.$refs.navbar.show = false;
    },
    setActiveMenuItem(target) {
      const navitems = this.$refs.navitem;

      if (!navitems) return;

      // Remove placeholder (invisible) items
      const filteredNavItems = navitems.filter((item) => {
        // If it has no 'getAttribute' method it is probably a dropdown component
        if (!item.getAttribute) return true;
        return !item.getAttribute('data-placeholder');
      });

      const index = filteredNavItems.findIndex((item) => {
        if (item instanceof Vue) return false; // It is a dropdown so it has no anchor
        const linkHref = item.firstChild.getAttribute('href');
        return linkHref.includes(target);
      });

      if (index >= 0) {
        const navItemWidth = 204;
        const translateValue = `${navItemWidth * index}px`;
        window.history.replaceState(undefined, undefined, target);
        this.$refs.marker.style = `transform: translateX(${translateValue});`;
      } else {
        this.restoreNavState();
      }
    },
    restoreNavState() {
      const { hash } = window.location;
      // Its possible that nav was destroyed, so no ref is available
      if (this.$refs.marker) this.$refs.marker.style = 'transform: translateX(-100%);';
      window.history.replaceState(undefined, undefined, window.location.href.replace(hash, ''));
    },
  },
};
</script>

<style lang="scss" scoped>
$header-background: $header-background-color;
$nav-link-color: $text-color--dark;
$nav-link-bg: $header-nav-link-background-color;
$nav-link-bg--hover: $header-nav-link-background-color--hover;
$nav-link-dropdown--hover: #B0D5D1;
.header-style {
  background-color: $header-background;
  transition: box-shadow 200ms ease;
  &.elevated { box-shadow:  0 0 4px 0px rgba(0, 0, 0, .5); }
}
:deep(.nav-bar-style) {
  padding: 0;
  height: 53px;
  .navbar-toggler {
    padding: 0;
    border: none;
    .navbar-toggler-icon {
      background-image: none;
      span {
        display: block;
        width: 100%;
        height: 4px;
        background-color: $nav-link-color;
        transition: all 200ms ease;
      }
    }
  }
  .navbar-brand {
    cursor: pointer;
    font-size: .875rem;
    font-weight: 600;
    margin: auto;
    color: $nav-link-color;
    &:hover { color: $nav-link-color; }
  }
  .navbar-collapse {
    .nav-item {
      width: 204px;
      text-align: center;
      &[data-placeholder] { display: none; }
      .nav-link {
        background: $nav-link-bg;
        color: $nav-link-color;
        font-size: .875rem;
        font-weight: 500;
        text-shadow: 0 0 1px $nav-link-color;
        padding: 16px;
        transition: background-color 200ms ease;
        white-space: normal;
        &:hover,
        &:focus {
          background: $nav-link-bg--hover;
          outline: none;
        }
      }
      &.dropdown.show {
        .nav-link {
          background: $nav-link-bg--hover;
          outline: none;
        }
        .navbar-nav__dropdown-icon {
          transform: rotate(-135deg);
          transform-origin: right;
        }
      }
      .navbar-nav__dropdown-label-content {
        position: relative;
        .navbar-nav__dropdown-text {
          margin-right: .5rem;
        }
        .navbar-nav__dropdown-icon {
          display: inline-block;
          width: .5rem;
          height: .5rem;
          border: 2px solid;
          border-color: transparent $nav-link-color $nav-link-color transparent;
          margin-bottom: .1rem;
          transform: rotate(45deg);
          transform-origin: bottom right;
          transition: transform 200ms linear;
        }
      }
      .navbar-nav__dropdown-content {
        width: 204px;
        top: calc(100% - 2px);
        border-radius: 0;
        border: none;
        background-color: $nav-link-bg--hover;
        margin-top: 0;
        padding-top: 0;
        padding-bottom: 0;
        .nav-link {
          text-align: center;
          background-color: transparent;
          &:hover { background-color: $nav-link-dropdown--hover; }
        }
      }
    }
    .navbar-nav__active-link-marker { display: none; }
  }
}

@include media-breakpoint-up(sm) {
  :deep(.nav-bar-style) {
    .navbar-brand {
      font-size: 1.25rem;
      transform: translateX(-28px);
    }
  }
}

@include media-breakpoint-up(lg) {
  :deep(.nav-bar-style) {
    padding-top: 0px;
    padding-bottom: 0px;
    .navbar-brand {
      font-size: 1rem;
      transform: none;
    }
    .navbar-nav { position: relative; }
    .navbar-collapse {
      .navbar-nav__active-link-marker,
      .navbar-nav__active-link-marker-hider {
        display: block;
        position: absolute;
        width: 204px;
        height: 4px;
        background-color: $nav-link-color;
        bottom: 0;
        left: 0;
        transition: all 200ms ease;
        transform: translateX(-100%);
      }
      .navbar-nav__active-link-marker-hider { background-color: $header-background-color; }
    }
  }
}
/****************************************************************
* Estiliza navegação para os breakpoints md e menor (n < 992px).
* Pareceu menos retrabalho fazer desta forma ao invés de aplicar
* os estilos em todos os breakpoints e depois reverter.
****************************************************************/
@include media-breakpoint-down(md) {
  :deep(.nav-bar-style) {
    .navbar-toggler[aria-expanded="true"] {
      position: fixed;
      z-index: 1;
      top: 15px;
      left: 24px;
      background-color: $header-background;
      padding: .75rem;
      border-radius: 50%;
      .navbar-toggler-icon { position: relative; }
      .navbar-toggler-icon > span {
        position: absolute;
        top: 50%;
      }
      .navbar-toggler-icon > span:nth-child(1) { transform: translateY(-2px) rotate(45deg); }
      .navbar-toggler-icon > span:nth-child(2) { opacity: 0; }
      .navbar-toggler-icon > span:nth-child(3) { transform: translateY(-2px) rotate(-45deg); }
      & ~ a.navbar-brand { visibility: hidden; }
      & ~ .navbar-collapse { display: block; }
    }
    .navbar-collapse {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0,0,0,.35);
      display: none;
      transition: none;
      overflow-y: scroll;
      .navbar-nav {
        margin-top: 120px;
        max-width: 488px;
        margin-left: auto;
        margin-right: auto;
        padding: 0 1rem;
        .nav-item {
          width: 100%;
          margin-bottom: 2rem;
          border-radius: 8px;
          overflow: hidden;
          .nav-link {
            padding: 1.5rem 0;
            font-size: 1.5rem;
          }
        }
        .navbar-nav__dropdown-content { width: 100%; }
      }
    }
  }
}
// Estilos apenas para telas menores que 576px
@include media-breakpoint-down(xs) {
  :deep(.nav-bar-style) {
    .navbar-collapse {
      .navbar-nav {
        .nav-item {
          margin-bottom: 1.25rem;
          .nav-link {
            padding: 1.25rem 0;
            font-size: 1.25rem;
          }
        }
      }
    }
  }
}
</style>
